import { collection, doc, getDocs, getDoc, increment, limit, orderBy, query, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const APP_TIMEZONE = 'America/Sao_Paulo';

const getLocalDateParts = (date: Date, timeZone: string = APP_TIMEZONE) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
};

export type LeaderboardEntry = {
  userId: string;
  nickname: string;
  photoURL?: string;
  score: number;
  periodId: string;
};

const getISOWeek = (date: Date) => {
  const { year, month, day } = getLocalDateParts(date);
  const tmp = new Date(Date.UTC(year, month - 1, day));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const getMonthId = (date: Date) => {
  const { year, month } = getLocalDateParts(date);
  return `${year}-${String(month).padStart(2, '0')}`;
};

export const updateLeaderboards = async (userId: string, nickname: string, photoURL: string | undefined, incrementValue: number) => {
  const now = new Date();
  const weekId = getISOWeek(now);
  const monthId = getMonthId(now);

  const entries = [
    { period: 'weekly', periodId: weekId },
    { period: 'monthly', periodId: monthId },
    { period: 'all', periodId: 'all' },
  ];

  for (const entry of entries) {
    const ref = doc(db, 'leaderboards', entry.period, entry.periodId, userId);
    await setDoc(ref, {
      nickname: nickname || 'Concurseiro',
      photoURL: photoURL || '',
      score: increment(incrementValue),
      periodId: entry.periodId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  }
};

export const getLeaderboard = async (period: 'weekly' | 'monthly' | 'all'): Promise<LeaderboardEntry[]> => {
  const now = new Date();
  const periodId = period === 'weekly' ? getISOWeek(now) : period === 'monthly' ? getMonthId(now) : 'all';
  const col = collection(db, 'leaderboards', period, periodId);
  const q = query(col, orderBy('score', 'desc'), limit(100));
  const snap = await getDocs(q);
  return snap.docs.map(d => {
    const data = d.data();
    return {
      userId: d.id,
      nickname: data.nickname || 'Concurseiro',
      photoURL: data.photoURL || '',
      score: data.score || 0,
      periodId
    } as LeaderboardEntry;
  });
};

export const getUserLeaderboardEntry = async (period: 'weekly' | 'monthly' | 'all', userId: string): Promise<LeaderboardEntry | null> => {
  const now = new Date();
  const periodId = period === 'weekly' ? getISOWeek(now) : period === 'monthly' ? getMonthId(now) : 'all';
  const docRef = doc(db, 'leaderboards', period, periodId, userId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      userId,
      nickname: data.nickname || 'Concurseiro',
      photoURL: data.photoURL || '',
      score: data.score || 0,
      periodId,
    } as LeaderboardEntry;
  }
  return null;
};

